import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
  Switch,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Add, BorderColorOutlined as Edit, Save, Close, DragIndicator, Search, FileDownloadOutlined } from '@mui/icons-material';
import { useSnackbar } from 'notistack';

import { DataAPIContext } from '../api';
import { escape_html, linkify } from '../chat/utils/chat_utils'
import { CREATE, DELETE, UPDATE } from '../api/dataProvider';
import { ReloadIcon } from '../components/actionIcons';
import DeleteButton from '../components/DeleteButton';
import DeleteIconButton from '../components/DeleteIconButton';
import RemoteAutocomplete from '../components/RemoteAutocomplete';
import { formatTourOptionLabel } from '../components/tourOptionLabel';
import { ContextStore } from '../store/contextStore';
import { isOk } from '../utils/errors';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import OutlinedExportButton from '../components/OutlinedExportButton';
import ConfirmationDialog from '../components/ConfirmationDialog';
import WidgetsPicker, { defaultWidgetIdsFor, widgetLabel } from './WidgetsPicker';
import { EMPTY_SCOPE, buildTagScope, featuredOf, plural, tagLabel } from './knowledgeReview/tagScope';
import { isWebWidgetAvailable } from './webWidgetAvailable';

const TRIGGER_RULES_RESOURCE = 'tour_operator/ai_auto_reply_rules';
const TOURS_RESOURCE = 'tour';
// Backend AIAutoReplyRule.hand_to_team (plan 2026-10-28-widget-rule-pass-to-team), web chat only.
const PASS_TO_TEAM_HELP = 'After this reply the chat is handed to your team, as with the fallback message: the '
  + 'assistant stops answering and the chat shows under "Needs a person". When "Ask for the visitor\'s email when '
  + 'handing off" is on, this reply also asks for their email.';

/* ====== helpers (tours) ====== */
const normalizeTour = (t) => {
  if (!t) return { id: null, name: '' };
  if (typeof t === 'string') return { id: null, name: t };
  if (typeof t === 'object') {
    const id = t.id ?? t._id ?? t.value ?? t?.data?.id ?? t?.pk ?? null;
    const name = t.name ?? t.title ?? t.label ?? t?.data?.name ?? t?.text ?? '';
    // Keep the bespoke/date fields when present so labels can show them.
    const extra = {};
    for (const k of ['is_bespoke', 'start_date', 'end_date']) {
      if (t[k] !== undefined) extra[k] = t[k];
    }
    return { id, name, ...extra };
  }
  return { id: null, name: String(t) };
};
const tourToId = (t) => normalizeTour(t).id;
const tourToName = (t) => normalizeTour(t).name;
const tourToLabel = (t) => formatTourOptionLabel(normalizeTour(t));
const toursEqual = (a, b) => {
  const A = normalizeTour(a); const B = normalizeTour(b);
  if (A.id && B.id) return String(A.id) === String(B.id);
  return tourToName(A).toLowerCase() === tourToName(B).toLowerCase();
};

function descendingComparator(a, b, orderBy) {
  const ax = a?.[orderBy];
  const bx = b?.[orderBy];
  const na = typeof ax === 'number' ? ax : (ax ?? '').toString();
  const nb = typeof bx === 'number' ? bx : (bx ?? '').toString();
  if (nb < na) return -1;
  if (nb > na) return 1;
  return 0;
}
function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}
function stableSort(array, comparator) {
  return array
    .map((el, index) => [el, index])
    .sort((a, b) => {
      const o = comparator(a[0], b[0]);
      return o !== 0 ? o : a[1] - b[1];
    })
    .map((el) => el[0]);
}

const collectionChipSx = {
  maxWidth: '100%',
  height: 'auto',
  '& .MuiChip-label': { whiteSpace: 'normal', wordBreak: 'break-word', display: 'block', padding: '4px 8px' },
};

const capitalize = (s) => s.charAt(0).toUpperCase() + s.slice(1);
const tagKey = (o) => `${o.kind}${o.id}`;
const withKind = (kind) => (t) => ({ ...t, kind });
const withoutKind = ({ kind, ...t }) => t;

/*
 * Tag scoping (knowledgeReview/tagScope.js, as trained answers): a rule applies to its tours, the
 * tours carrying any of its System tags (`collections`, any category) and the tours carrying any of
 * its Access tags -- every tour when it names none -- minus its `excluded_tours` either way. A tag
 * covers tours added to it later.
 *
 * TagPicker: a multi-select whose checkbox list stays open while you tick, with how many tours each
 * tag covers. Options and values carry `kind` ('tag' | 'atag').
 */
function TagPicker({ label, options, value, onChange, disabled, groupBy, chipLabel }) {
  // A tag the rule still names but no tour carries any more is not offered; keep it listed.
  const all = [...options, ...value.filter((v) => !options.some((o) => tagKey(o) === tagKey(v)))];
  return (
    <Autocomplete
      multiple
      disableCloseOnSelect
      size="small"
      disabled={disabled}
      options={all}
      value={value}
      onChange={(e, v) => onChange(v || [])}
      getOptionLabel={(o) => o.name}
      isOptionEqualToValue={(a, b) => tagKey(a) === tagKey(b)}
      groupBy={groupBy}
      renderOption={(props, o, { selected }) => (
        <li {...props} key={tagKey(o)}>
          <Checkbox size="small" checked={selected} tabIndex={-1} disableRipple sx={{ p: 0.25, mr: 1 }} />
          {o.name}
          {o.product_count ? (
            <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
              {o.product_count} tour{o.product_count === 1 ? '' : 's'}
            </Typography>
          ) : null}
        </li>
      )}
      renderTags={(v, getTagProps) => v.map((option, index) => (
        <Chip {...getTagProps({ index })} label={chipLabel(option)} size="small" color="primary" variant="outlined" />
      ))}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          inputProps={{ ...params.inputProps, 'aria-label': label }}
          sx={{ backgroundColor: 'white', '& .MuiFormLabel-root': { fontSize: 12 } }}
        />
      )}
      sx={{ width: '100%', mb: 1 }}
    />
  );
}

// One picker per featured category (Crown: Collections, Destinations), then "Other tags": every
// other System tag and the Access tags. `onChange` gets { collections, access_tags }.
function RuleTagPickers({ scope, draft, onChange, disabled }) {
  const tags = draft.collections || [];
  const atags = draft.access_tags || [];
  const others = tags.filter((t) => !featuredOf(scope, t));
  const otherOptions = [...scope.otherTags.map(withKind('tag')), ...scope.accessTags.map(withKind('atag'))];
  return (
    <>
      {scope.featured.map((f) => {
        const inF = (t) => featuredOf(scope, t)?.code === f.code;
        return (
          <TagPicker
            key={f.code}
            label={capitalize(plural(f.name.toLowerCase()))}
            options={f.tags.map(withKind('tag'))}
            value={tags.filter(inF).map(withKind('tag'))}
            chipLabel={(o) => o.name}
            disabled={disabled}
            onChange={(v) => onChange({ collections: [...tags.filter((t) => !inF(t)), ...v.map(withoutKind)] })}
          />
        );
      })}
      {(otherOptions.length > 0 || others.length > 0 || atags.length > 0) && (
        <TagPicker
          label="Other tags"
          options={otherOptions}
          value={[...others.map(withKind('tag')), ...atags.map(withKind('atag'))]}
          groupBy={(o) => (o.kind === 'atag' ? 'Access tags' : o.category?.name || 'Tags')}
          chipLabel={(o) => tagLabel(o, scope, o.kind)}
          disabled={disabled}
          onChange={(v) => onChange({
            collections: [...tags.filter((t) => featuredOf(scope, t)), ...v.filter((o) => o.kind === 'tag').map(withoutKind)],
            access_tags: v.filter((o) => o.kind === 'atag').map(withoutKind),
          })}
        />
      )}
    </>
  );
}

// Tours left out of a rule, whichever tag or pick covers them ("All tours except …" when it
// names none). Same tour search as the Tours picker.
function ExceptToursPicker({ value, onChange, disabled, compact }) {
  const [query, setQuery] = useState('');
  return (
    <RemoteAutocomplete
      disabled={disabled}
      multiple
      style={{ backgroundColor: 'white', ...(compact ? {} : { width: '100%' }) }}
      resource={TOURS_RESOURCE}
      apiParams={{ fields: 'id,name,is_bespoke,start_date,end_date' }}
      value={(value || []).map(normalizeTour)}
      onChange={(e, v) => onChange((v || []).map(normalizeTour))}
      setOptionsCount={() => {}}
      filterOptions={(options) => options}
      renderTags={(v, getTagProps) => v.map((option, index) => (
        <Chip
          {...getTagProps({ index })}
          label={tourToLabel(option)}
          size="small"
          sx={{ height: 'auto', maxWidth: compact ? '180px' : undefined, '& .MuiChip-label': { whiteSpace: 'normal', wordBreak: 'break-word', display: 'block', fontSize: 12, padding: '3px 8px' } }}
        />
      ))}
      size="small"
      label="Except tours"
      variant="outlined"
      sx={{
        mt: 1,
        ...(compact ? { minWidth: 200 } : { width: '100%' }),
        '& .MuiInputBase-root': { minHeight: 32, py: 0.25, flexWrap: 'wrap' },
        '& .MuiFormLabel-root': { fontSize: 12 },
      }}
      textFieldProps={{
        InputLabelProps: {
          sx: {
            fontSize: 12,
            lineHeight: 1.2,
            transform: 'translate(14px, 10px) scale(1)',
            '&.MuiInputLabel-shrink': { transform: 'translate(14px, -6px) scale(0.85)' },
          },
        },
      }}
      clearOnBlur={false}
      noOptionsText={query.trim() ? `No results for "${query.trim()}"` : 'Type to search'}
      getOptionLabel={(opt) => tourToLabel(opt)}
      isOptionEqualToValue={(opt, val) => toursEqual(opt, val)}
      onQueryChange={setQuery}
    />
  );
}

// A rule's tours column outside editing: tags, tours, "All tours" when it names none, exclusions.
function RuleScopeChips({ rule, scope }) {
  const tags = [
    ...(rule.collections || []).map((c) => ({ key: `c${c.id}`, label: tagLabel(c, scope) })),
    ...(rule.access_tags || []).map((a) => ({ key: `a${a.id}`, label: tagLabel(a, scope, 'atag') })),
  ];
  const tours = rule.tours || [];
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
      {tags.map((c) => <Chip key={c.key} label={c.label} size="small" color="primary" variant="outlined" sx={collectionChipSx} />)}
      {tours.map((t, i) => (
        <Chip key={`${tourToId(t) ?? tourToName(t)}-${i}`} label={tourToLabel(t)} size="small" sx={collectionChipSx} />
      ))}
      {!tags.length && !tours.length && <Typography variant="caption" color="text.secondary">All tours</Typography>}
      {(rule.excluded_tours || []).map((t, i) => (
        <Chip
          key={`x${tourToId(t) ?? tourToName(t)}-${i}`}
          label={`Except ${tourToLabel(t)}`}
          size="small"
          variant="outlined"
          sx={{ ...collectionChipSx, borderStyle: 'dashed' }}
        />
      ))}
    </Box>
  );
}

function AutoReplyRulesTable({ onRulesCountChange, licenseeId, channelScope, widgets = [], widget = null }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { enqueueSnackbar } = useSnackbar();
  const dataAPI = useContext(DataAPIContext);
  const { hasPerm, tourOperator } = useContext(ContextStore);
  // CP-8187: on the Web Chat Widget page this table manages the widget's own rules,
  // which live in the web_widget.* scope; everywhere else it manages support-chat
  // rules and keeps the ai.* scope. `channelScope` is what tells the two apart.
  const isWidgetTable = channelScope === 'web_widget';
  const permScope = isWidgetTable ? 'web_widget' : 'ai';
  const hasAIlistPerm = hasPerm(permScope, isWidgetTable ? 'read' : 'list');
  const hasAIwritePerm = hasPerm(permScope, 'write');
  const hasAIdeletePerm = hasPerm(permScope, 'delete');
  // A "both"-scoped rule shown on the widget page also drives the support
  // auto-responder, so editing it needs the support scope too -- holding
  // web_widget.write alone must not reach into support chat.
  const canWriteRule = (rule) =>
    hasAIwritePerm
    && (!isWidgetTable || (rule?.source_scope || 'both') === 'web_widget' || hasPerm('ai', 'write'));
  const canDeleteRule = (rule) =>
    hasAIdeletePerm
    && (!isWidgetTable || (rule?.source_scope || 'both') === 'web_widget' || hasPerm('ai', 'delete'));
  // Channel scoping (Both / Web widget / Support chat) is only meaningful when the
  // operator actually has the web chat widget feature; otherwise hide the column,
  // filter, and editor toggle entirely (rules stay implicitly "both").
  const webWidgetAvailable = isWebWidgetAvailable(tourOperator);
  const SCOPE_LABELS = { both: 'Both', web_widget: 'Web widget', chat: 'Support chat' };
  // channelScope pins this table to one channel (e.g. 'web_widget' on the Web Widget
  // page, 'chat' on the support AI Suite): new rules default to that scope, the list
  // shows only that scope + 'both' (shared), and the per-row scope picker is replaced
  // by a read-only chip. Unset = legacy behaviour (all scopes, editable picker).
  const scopeLocked = !!channelScope;
  const defaultScope = channelScope || 'both';
  // When pinned to a channel (support AI Suite / widget page are now separate features),
  // the "Applies to" column and filter are redundant -- hide them entirely. They only
  // appear in the legacy (unpinned) view where a single table shows every channel.
  const showScopeCol = webWidgetAvailable && !scopeLocked;
  const scopeEditable = showScopeCol;
  // Widget rules answer anonymous website visitors, who aren't tied to any tour --
  // per-tour targeting is meaningless there, so drop the Tours column and editor.
  // Existing tour links (on shared "both" rules) are preserved untouched on save.
  const showToursCol = !isWidgetTable;
  // CP-8335: an operator with several branded web widgets sees the rules of the widget picked
  // on the page (unassigned + its own, or only its own when isolated) and assigns rules to
  // widgets in a Widgets column.
  const multiWidget = isWidgetTable && widgets.length > 1 && !!widget;
  const showWidgetsCol = multiWidget;
  const widgetUsesRule = (rule) => {
    if (!multiWidget) return true;
    const assigned = (rule.widgets || []).map((w) => w.id);
    if (assigned.includes(widget.id)) return true;
    return widget.knowledge_mode !== 'isolated' && assigned.length === 0;
  };

  // The tags a rule can be scoped to, offered above Tours: featured categories first, then every
  // other System tag and the Access tags (empty for an operator whose tours carry none).
  const [tagScope, setTagScope] = useState(EMPTY_SCOPE);
  useEffect(() => {
    if (!showToursCol || !hasAIlistPerm) return undefined;
    let active = true;
    Promise.resolve(dataAPI?.('GET_ONE', TRIGGER_RULES_RESOURCE, {
      urlSuffix: 'collections/',
      filter: licenseeId ? { tour_operator: licenseeId } : {},
    })).then((res) => {
      if (!active || !res?.data) return;
      // A backend before tag scoping sends one collection category: collection_category + results.
      setTagScope(buildTagScope({ ...res.data, collections: res.data.results }));
    }).catch(() => { });
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showToursCol, licenseeId]);
  const showTagPickers = showToursCol
    && (tagScope.featured.length > 0 || tagScope.otherTags.length > 0 || tagScope.accessTags.length > 0);
  const toursLabel = showTagPickers ? 'Other tours (leave all empty for all tours)' : 'Tours (leave empty for all tours)';
  // "Collections & tours", "Collections, destinations & tours", "Tags & tours".
  const toursHeader = (() => {
    if (!showTagPickers) return 'Tours';
    const kinds = tagScope.featured.length ? tagScope.featured.map((f) => plural(f.name.toLowerCase())) : ['tags'];
    return capitalize(`${kinds.join(', ')} & tours`);
  })();

  const tableContainerRef = React.useRef(null);
  const newRuleRowRef = React.useRef(null);

  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [rules, setRules] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [channelFilter, setChannelFilter] = useState('all');
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('priority'); // default to priority so moves are visible

  const [selection, setSelection] = useState({});
  const [editingId, setEditingId] = useState(null);
  const [drafts, setDrafts] = useState({});
  const patchDraft = (rule, patch) => setDrafts((d) => ({ ...d, [rule.id]: { ...(d[rule.id] ?? rule), ...patch } }));
  const [ruleToDelete, setRuleToDelete] = useState(null); // single rule to delete (for confirmation dialog)
  const [rulesToDelete, setRulesToDelete] = useState(null); // multiple rules to delete (for confirmation dialog)

  // per-row RemoteAutocomplete state
  const [tourQueryByRule, setTourQueryByRule] = useState({});
  const [, setTourOptionsByRule] = useState({});

  const toggleOne = (id) => setSelection((s) => ({ ...s, [id]: !s[id] }));

  const mapServerRule = (r) => ({
    id: r.id ?? r._id,
    intent_description: r.intent_description ?? '',
    intent_label: r.intent_label ?? r.trigger ?? '',
    reply_message: r.reply_message ?? r.message ?? '',
    tours: (r.tours || r.tour_ids || []).map(normalizeTour),
    collections: r.collections || [],
    access_tags: r.access_tags || [],
    excluded_tours: (r.excluded_tours || []).map(normalizeTour),
    widgets: r.widgets || [],
    min_confidence: r.min_confidence ?? 0.6,
    is_enabled: (typeof r.is_enabled === 'boolean') ? r.is_enabled : true,
    source_scope: r.source_scope || 'both',
    hand_to_team: !!r.hand_to_team,
    priority: Number(r.priority ?? r.order ?? 100),
    createdAt: r.created_ts ? new Date(r.created_ts).getTime() : Date.now(),
  });

  const fetchRules = async () => {
    if (!hasAIlistPerm) { setLoading(false); return; }
    setLoading(true);
    try {
      const res = await dataAPI?.('GET_LIST', TRIGGER_RULES_RESOURCE, licenseeId ? { franchise_to: licenseeId } : {});
      if (Array.isArray(res)) setRules(res.map(mapServerRule));
    } catch {
      /* keep old view */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRules();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [licenseeId]);

  useEffect(() => {
    onRulesCountChange?.(rules.filter(r => !String(r.id).startsWith('local-')).length);
  }, [rules, onRulesCountChange]);

  // Mobile has no checkboxes, so drop any selection made on a wider viewport —
  // otherwise a resize leaves rules selected with nothing on screen to show it.
  useEffect(() => {
    if (isMobile) setSelection({});
  }, [isMobile]);

  // Filter rules based on search query
  const filteredRules = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    return rules.filter(rule => {
      // Always show the row being edited, even if it doesn't match the filters
      if (editingId && rule.id === editingId) return true;

      // Channel scoping. When pinned (scopeLocked) show only this channel + shared
      // ('both') rules. Otherwise honour the "Applies to" dropdown ('all' = no filter).
      const ruleScope = rule.source_scope || 'both';
      if (scopeLocked) {
        if (ruleScope !== channelScope && ruleScope !== 'both') return false;
      } else if (channelFilter !== 'all' && ruleScope !== channelFilter) {
        return false;
      }
      if (!widgetUsesRule(rule)) return false;

      if (!query) return true;
      // Search in description
      if (rule.intent_description?.toLowerCase().includes(query)) return true;
      // Search in trigger/intent
      if (rule.intent_label?.toLowerCase().includes(query)) return true;
      // Search in message
      if (rule.reply_message?.toLowerCase().includes(query)) return true;
      // Search in tour names (only when the Tours column is shown at all)
      if (showToursCol && rule.tours?.some(t => tourToLabel(t).toLowerCase().includes(query))) return true;
      if (showToursCol && rule.collections?.some(c => c.name.toLowerCase().includes(query))) return true;
      if (showToursCol && rule.access_tags?.some(a => a.name.toLowerCase().includes(query))) return true;
      return false;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rules, searchQuery, editingId, channelFilter, channelScope, scopeLocked, showToursCol, multiWidget, widget]);

  // Select all logic (works with filtered results)
  const allSelected = useMemo(
    () => filteredRules.length && Object.values(selection).filter(Boolean).length === filteredRules.length,
    [filteredRules, selection]
  );
  const toggleAll = (checked) =>
    setSelection(checked ? Object.fromEntries(filteredRules.map((r) => [r.id, true])) : {});

  // Bulk delete is only offered when every selected rule is individually deletable
  // (on the widget page a shared "both" rule needs the support scope as well).
  const selectedRulesDeletable = useMemo(
    () => rules.filter((r) => selection[r.id]).every((r) => canDeleteRule(r)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rules, selection, hasAIdeletePerm, isWidgetTable]
  );

  // Position map across the whole list for reordering (uses all rules, not just filtered)
  const sortedAllRules = useMemo(
    () => stableSort(rules, getComparator(order, orderBy)),
    [rules, order, orderBy]
  );

  // Next priority for new rows
  const nextPriority = useMemo(() => {
    const nums = rules.map((r) => (typeof r.priority === 'number' ? r.priority : 0));
    return (nums.length ? Math.max(...nums) : 0) + 1;
  }, [rules]);

  const startEdit = (rule) => {
    setEditingId(rule.id);
    setDrafts((d) => ({
      ...d,
      [rule.id]: {
        ...rule,
        tours: (rule.tours || []).map(normalizeTour),
        priority: typeof rule.priority === 'number' ? rule.priority : nextPriority,
      },
    }));
    setTourQueryByRule((q) => ({ ...q, [rule.id]: '' }));
    setTourOptionsByRule((o) => ({ ...o, [rule.id]: (rule.tours || []).map(normalizeTour) }));
  };
  const cancelEdit = () => {
    // If canceling a new rule (local-), remove it from the list
    if (editingId && String(editingId).startsWith('local-')) {
      setRules((r) => r.filter((x) => x.id !== editingId));
      // Clean up any draft state for this rule
      setDrafts((d) => {
        const next = { ...d };
        delete next[editingId];
        return next;
      });
      // Clean up tour autocomplete state
      setTourQueryByRule((q) => {
        const next = { ...q };
        delete next[editingId];
        return next;
      });
      setTourOptionsByRule((o) => {
        const next = { ...o };
        delete next[editingId];
        return next;
      });
    }
    setEditingId(null);
  };

  const addRule = () => {
    const now = Date.now();
    const id = `local-${now}`;
    const newRule = {
      id,
      intent_description: '',
      intent_label: '',
      reply_message: '',
      tours: [],
      collections: [],
      access_tags: [],
      excluded_tours: [],
      // A rule added while editing one site's widget belongs to that widget (CP-8335).
      widgets: multiWidget
        ? widgets.filter((w) => defaultWidgetIdsFor(widgets, widget).includes(w.id))
          .map((w) => ({ id: w.id, key: w.key, name: w.display_name || w.name }))
        : [],
      min_confidence: 0.6,
      is_enabled: true,
      source_scope: defaultScope,
      hand_to_team: false,
      priority: nextPriority,
      createdAt: now
    };
    setRules((r) => [newRule, ...r]);
    startEdit(newRule);
    // Scroll to bottom after DOM updates to show the new row
    setTimeout(() => {
      if (tableContainerRef.current) {
        tableContainerRef.current.scrollTop = tableContainerRef.current.scrollHeight;
      }
    }, 100);
  };

  // Scroll to newly added/edited row
  useEffect(() => {
    if (editingId && newRuleRowRef.current && tableContainerRef.current) {
      // Small delay to ensure DOM is fully updated
      setTimeout(() => {
        newRuleRowRef.current?.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center' 
        });
      }, 100);
    }
  }, [editingId]);

  const buildPayload = (ruleLike) => ({
    intent_description: ruleLike.intent_description || '',
    intent_label: (ruleLike.intent_label || '').trim().replace(/\s/g, '_'),
    reply_message: ruleLike.reply_message || '',
    min_confidence: Number(ruleLike.min_confidence ?? 0.6),
    is_enabled: !!ruleLike.is_enabled,
    source_scope: ruleLike.source_scope || defaultScope,
    tour_ids: (ruleLike.tours || []).map(tourToId).filter(Boolean),
    // System tags of any category (the key predates tag scoping), Access tags, tours left out.
    collection_ids: (ruleLike.collections || []).map((c) => c.id),
    atag_ids: (ruleLike.access_tags || []).map((a) => a.id),
    excluded_tour_ids: (ruleLike.excluded_tours || []).map(tourToId).filter(Boolean),
    ...(multiWidget ? { widget_ids: (ruleLike.widgets || []).map((w) => w.id) } : {}),
    // Only the web chat page sets it; a save from the support page leaves it as it is.
    ...(isWidgetTable ? { hand_to_team: !!ruleLike.hand_to_team } : {}),
    priority: Number(ruleLike.priority ?? nextPriority),
  });

  const saveRule = async (id) => {
    const draft = drafts[id] || {};
    if (!draft.intent_label?.trim() || !draft.reply_message?.trim()) {
      enqueueSnackbar('Please fill Trigger and Message', { variant: 'warning' });
      return;
    }
    // Sanitize intent_label: replace spaces with underscores
    const sanitizedDraft = {
      ...draft,
      intent_label: (draft.intent_label || '').trim().replace(/\s/g, '_')
    };
    const payload = buildPayload(sanitizedDraft);

    // optimistic
    setRules((prev) => prev.map((r) => (r.id === id ? { ...r, ...sanitizedDraft } : r)));
    setEditingId(null);

    try {
      if (String(id).startsWith('local-')) {
        const createData = licenseeId ? { ...payload, tour_operator: licenseeId } : payload;
        const res = await dataAPI?.(CREATE, TRIGGER_RULES_RESOURCE, { data: createData });
        const newId = res?.data?.id ?? res?.id;
        if (!newId) throw new Error('No ID returned from server');
        setRules((prev) => prev.map((r) => (r.id === id ? { ...r, id: newId } : r)));
      } else {
        await dataAPI?.(UPDATE, TRIGGER_RULES_RESOURCE, { id, data: payload });
      }
      enqueueSnackbar('Rule saved', { variant: 'success' });
    } catch {
      enqueueSnackbar('Failed to save rule', { variant: 'error' });
      fetchRules(); // rollback to server state
    }
  };

  const handleDeleteRule = (rule) => {
    setRuleToDelete(rule);
  };

  const confirmDeleteRule = async () => {
    if (!ruleToDelete) return;
    const rule = ruleToDelete;
    setRuleToDelete(null);
    const prev = rules;
    setRules((r) => r.filter((x) => x.id !== rule.id));
    try {
      if (!String(rule.id).startsWith('local-')) {
        await dataAPI?.(DELETE, TRIGGER_RULES_RESOURCE, { id: rule.id });
      }
      enqueueSnackbar('Rule deleted', { variant: 'success' });
    } catch {
      enqueueSnackbar('Failed to delete', { variant: 'error' });
      setRules(prev);
    }
  };

  const cancelDeleteRule = () => {
    setRuleToDelete(null);
  };

  const handleBulkDelete = () => {
    const ids = Object.entries(selection).filter(([, v]) => v).map(([id]) => id);
    if (!ids.length) return;
    setRulesToDelete(ids);
  };

  const confirmBulkDelete = async () => {
    if (!rulesToDelete || !rulesToDelete.length) return;
    const ids = rulesToDelete;
    setRulesToDelete(null);
    const prev = rules;
    setRules((r) => r.filter((x) => !ids.includes(x.id)));
    setSelection({});
    try {
      await Promise.all(ids.map((id) =>
        String(id).startsWith('local-')
          ? Promise.resolve()
          : dataAPI?.(DELETE, TRIGGER_RULES_RESOURCE, { id }).catch(() => null)
      ));
      enqueueSnackbar('Selected rules deleted', { variant: 'success' });
    } catch {
      enqueueSnackbar('Some deletions may have failed', { variant: 'warning' });
      setRules(prev);
    }
  };

  const cancelBulkDelete = () => {
    setRulesToDelete(null);
  };

  // Export rules to JSON
  const handleExport = () => {
    try {
      const exportData = rules.map(rule => ({
        intent_description: rule.intent_description || '',
        intent_label: rule.intent_label || '',
        reply_message: rule.reply_message || '',
        tours: (rule.tours || []).map(t => ({
          id: tourToId(t),
          name: tourToName(t)
        })),
        collections: (rule.collections || []).map(({ id, name }) => ({ id, name })),
        access_tags: (rule.access_tags || []).map(({ id, name }) => ({ id, name })),
        excluded_tours: (rule.excluded_tours || []).map((t) => ({ id: tourToId(t), name: tourToName(t) })),
        min_confidence: rule.min_confidence ?? 0.6,
        is_enabled: rule.is_enabled ?? true,
        ...(isWidgetTable ? { hand_to_team: !!rule.hand_to_team } : {}),
        priority: rule.priority ?? 100,
      }));

      const jsonStr = JSON.stringify(exportData, null, 2);
      const blob = new Blob([jsonStr], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `autoreply-rules-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      enqueueSnackbar('Rules exported successfully', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar('Failed to export rules', { variant: 'error' });
    }
  };

  /**
   * Import rules from a JSON file.
   * 
   * Opens a file picker to select a JSON file containing an array of rule objects.
   * For each rule in the file:
   * - If a rule with the same intent_label exists, it will be updated with the imported data
   * - If no matching rule exists, a new rule will be created
   * 
   * Rules are matched by intent_label (after sanitization: spaces replaced with underscores).
   * This allows re-importing to update existing rules rather than creating duplicates.
   * 
   * Expected JSON format:
   * [
   *   {
   *     "intent_description": "...",
   *     "intent_label": "...",
   *     "reply_message": "...",
   *     "tours": [{"id": 123, "name": "Tour Name"}],  // Tour ID is used for matching. Tour name is optional/redundant.
   *     "min_confidence": 0.6,
   *     "is_enabled": true,
   *     "priority": 1
   *   }
   * ]
   * 
   * Required fields: intent_label, reply_message
   * Optional fields: intent_description, tours, min_confidence, is_enabled, priority
   * 
   * Note: For tours, only the tour ID is used to match tours in the system. Tour names
   * may be included in the JSON but are not used for matching - they are redundant.
   */
  const handleImport = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';
    input.onchange = async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;

      setImporting(true);
      try {
        const text = await file.text();
        const importedData = JSON.parse(text);

        if (!Array.isArray(importedData)) {
          enqueueSnackbar('Invalid file format: expected an array of rules', { variant: 'error' });
          return;
        }

        let successCount = 0;
        let updateCount = 0;
        let createCount = 0;
        let errorCount = 0;

        for (const ruleData of importedData) {
          try {
            // Validate required fields
            if (!ruleData.intent_label?.trim() || !ruleData.reply_message?.trim()) {
              errorCount++;
              continue;
            }

            // Sanitize intent_label for matching (same as how it's stored)
            const sanitizedIntentLabel = (ruleData.intent_label || '').trim().replace(/\s/g, '_');

            // Check if a rule with this intent_label already exists
            const existingRule = rules.find(r => 
              (r.intent_label || '').trim().replace(/\s/g, '_') === sanitizedIntentLabel
            );

            // Extract tour IDs from imported data. Tour ID is used to match tours.
            // Tour name is optional/redundant - it's not used for matching, only ID matters.
            const tourIds = (ruleData.tours || [])
              .map(t => {
                if (typeof t === 'string') {
                  // If it's a string, try to extract ID if it's an object-like string, otherwise skip
                  return null;
                }
                // Extract ID from tour object (handles various formats via normalizeTour)
                return tourToId(normalizeTour(t));
              })
              .filter(Boolean); // Only keep valid IDs

            const payload = {
              intent_description: ruleData.intent_description || '',
              intent_label: sanitizedIntentLabel,
              reply_message: ruleData.reply_message || '',
              min_confidence: Number(ruleData.min_confidence ?? 0.6),
              is_enabled: typeof ruleData.is_enabled === 'boolean' ? ruleData.is_enabled : true,
              tour_ids: tourIds,
              // Tags and exclusions by id, as tours; absent in the file = left as they are.
              ...(Array.isArray(ruleData.collections)
                ? { collection_ids: ruleData.collections.map((c) => c?.id).filter(Boolean) }
                : {}),
              ...(Array.isArray(ruleData.access_tags)
                ? { atag_ids: ruleData.access_tags.map((a) => a?.id).filter(Boolean) }
                : {}),
              ...(Array.isArray(ruleData.excluded_tours)
                ? { excluded_tour_ids: ruleData.excluded_tours.map((t) => t?.id).filter(Boolean) }
                : {}),
              ...(isWidgetTable && typeof ruleData.hand_to_team === 'boolean'
                ? { hand_to_team: ruleData.hand_to_team }
                : {}),
              priority: Number(ruleData.priority ?? (existingRule?.priority ?? nextPriority)),
            };

            if (existingRule && !String(existingRule.id).startsWith('local-')) {
              // Update existing rule
              await dataAPI?.(UPDATE, TRIGGER_RULES_RESOURCE, { id: existingRule.id, data: payload });
              updateCount++;
            } else {
              // Create new rule
              await dataAPI?.(CREATE, TRIGGER_RULES_RESOURCE, { data: licenseeId ? { ...payload, tour_operator: licenseeId } : payload });
              createCount++;
            }
            successCount++;
          } catch (error) {
            errorCount++;
          }
        }

        if (successCount > 0) {
          const messages = [];
          if (updateCount > 0) messages.push(`${updateCount} updated`);
          if (createCount > 0) messages.push(`${createCount} created`);
          const message = `Imported ${successCount} rule${successCount > 1 ? 's' : ''} (${messages.join(', ')})${errorCount > 0 ? `, ${errorCount} failed` : ''}`;
          enqueueSnackbar(
            message,
            { variant: successCount > 0 && errorCount === 0 ? 'success' : 'warning' }
          );
          fetchRules(); // Refresh the list
        } else {
          enqueueSnackbar('Failed to import any rules', { variant: 'error' });
        }
      } catch (error) {
        enqueueSnackbar('Failed to parse JSON file', { variant: 'error' });
      } finally {
        setImporting(false);
      }
    };
    input.click();
  };

  // Toggle enabled (optimistic). Tries partial update first, falls back to full payload.
  const toggleEnabled = async (rule, value) => {
    // optimistic UI
    setRules((prev) => prev.map((r) => (r.id === rule.id ? { ...r, is_enabled: value } : r)));

    if (String(rule.id).startsWith('local-')) return; // nothing to persist yet

    try {
      await dataAPI?.(UPDATE, TRIGGER_RULES_RESOURCE, { id: rule.id, data: { is_enabled: value } });
      enqueueSnackbar('Rule ' + (value ? 'enabled' : 'disabled'), { variant: 'success' });
    } catch {
      try {
        const full = buildPayload({ ...rule, is_enabled: value });
        await dataAPI?.(UPDATE, TRIGGER_RULES_RESOURCE, { id: rule.id, data: full });
      } catch {
        enqueueSnackbar('Failed to update Enabled state', { variant: 'error' });
        // rollback
        setRules((prev) => prev.map((r) => (r.id === rule.id ? { ...r, is_enabled: !value } : r)));
      }
    }
  };

  // "Pass to team" (optimistic, like Enabled). While the row is being edited it is part of the
  // draft and goes out with Save instead.
  const toggleHandToTeam = async (rule, value) => {
    setRules((prev) => prev.map((r) => (r.id === rule.id ? { ...r, hand_to_team: value } : r)));
    const res = await Promise.resolve(
      dataAPI?.(UPDATE, TRIGGER_RULES_RESOURCE, { id: rule.id, data: { hand_to_team: value } }),
    ).catch(() => null);
    if (isOk(res)) {
      enqueueSnackbar(value ? 'The chat goes to your team after this reply' : 'The assistant keeps answering after this reply',
        { variant: 'success' });
      return;
    }
    enqueueSnackbar('Failed to update "Pass to team"', { variant: 'error' });
    setRules((prev) => prev.map((r) => (r.id === rule.id ? { ...r, hand_to_team: !value } : r)));
  };
  const handToTeamSwitch = (rule, isEditing, draft, rowWrite, sx) => (
    <Switch
      size="small"
      checked={!!(isEditing ? draft.hand_to_team : rule.hand_to_team)}
      onChange={(e) => (isEditing
        ? patchDraft(rule, { hand_to_team: e.target.checked })
        : toggleHandToTeam(rule, e.target.checked))}
      disabled={!rowWrite}
      sx={sx}
      inputProps={{ 'aria-label': 'pass the chat to the team' }}
    />
  );

  // Reorder using drag and drop (must be sorted by priority)
  const handleDragEnd = async (result) => {
    if (!result.destination) return; // dropped outside
    if (result.destination.index === result.source.index) return; // no change

    if (orderBy !== 'priority') {
      enqueueSnackbar('Switch sorting to "Priority" to reorder.', { variant: 'info' });
      return;
    }

    const list = [...sortedAllRules]; // current global order
    const sourceIndex = result.source.index;
    const destIndex = result.destination.index;

    // Move item from source to destination
    const [movedItem] = list.splice(sourceIndex, 1);
    list.splice(destIndex, 0, movedItem);

    // renumber 1..N
    const withNewPrio = list.map((r, idx) => ({ ...r, priority: idx + 1 }));

    // find which changed
    const changed = withNewPrio
      .filter((nr) => rules.find((o) => o.id === nr.id)?.priority !== nr.priority)
      .map((nr) => ({ id: nr.id, priority: nr.priority }));

    if (!changed.length) return;

    // optimistic UI
    const newPriorityMap = new Map(withNewPrio.map((r) => [r.id, r.priority]));
    setRules((prev) => prev.map((r) =>
      newPriorityMap.has(r.id) ? { ...r, priority: newPriorityMap.get(r.id) } : r
    ));

    // persist only { priority } for changed non-local rows
    try {
      await Promise.all(
        changed
          .filter((c) => !String(c.id).startsWith('local-'))
          .map((c) => dataAPI?.(UPDATE, TRIGGER_RULES_RESOURCE, { id: c.id, data: { priority: c.priority } }))
      );
    } catch {
      enqueueSnackbar('Failed to reorder, reloading…', { variant: 'error' });
      fetchRules(); // rollback
    }
  };

  // Sorted list for display
  const sortedFiltered = useMemo(() => {
    return stableSort(filteredRules, getComparator(order, orderBy));
  }, [filteredRules, order, orderBy]);


  if (!hasAIlistPerm) {
    return (
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6">Access Denied</Typography>
        <Typography>You do not have permission to view these rules.</Typography>
      </Paper>
    );
  }

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 3, paddingBottom: 0, borderRadius: '12px' }}>
      {/* Title and Description */}
      <Box mb={isMobile ? 2 : 1.5}>
        <Typography variant="h6">Trigger-Reply Use Cases</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Configure replies that trigger when specified intents are detected in guest messages.
        </Typography>
      </Box>

      {/* Action Buttons — natural-width wrapping row, same as KnowledgeBaseManager */}
      <Box
        display="flex"
        alignItems="center"
        justifyContent={isMobile ? 'flex-start' : 'flex-end'}
        gap={isMobile ? 1.7 : 1}
        mb={2}
        sx={{ flexWrap: 'wrap' }}
      >
        <IconButton onClick={fetchRules} title="Refresh" sx={{ mr: isMobile ? -1.3 : -1.5 }}>
          <ReloadIcon />
        </IconButton>
        {!isMobile && (
          <DeleteButton
            onClick={handleBulkDelete}
            disabled={!Object.values(selection).some(Boolean) || !selectedRulesDeletable}
          >
            Delete Selected
          </DeleteButton>
        )}
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={addRule}
          disabled={!hasAIwritePerm || (editingId != null)}
        >
          New Trigger-Reply
        </Button>
      </Box>

      {/* Search and Export/Import */}
      <Box 
        mb={2} 
        display="flex" 
        flexDirection={isMobile ? 'column' : 'row'}
        alignItems={isMobile ? 'stretch' : 'center'}
        justifyContent="space-between"
        gap={2}
      >
        {/* Search + filter grouped on the left so the filter sits beside the
            search rather than floating in the middle of the toolbar. */}
        <Box
          display="flex"
          flexDirection={isMobile ? 'column' : 'row'}
          alignItems={isMobile ? 'stretch' : 'center'}
          gap={2}
          sx={{ flex: 1, minWidth: 0 }}
        >
          <TextField
            variant="filled"
            size="small"
            placeholder={
              isMobile
                ? "Search..."
                : showToursCol
                  ? "Search rules by description, trigger, message, or tour name..."
                  : "Search rules by description, trigger, or message..."
            }
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{
              width: '100%',
              maxWidth: isMobile ? '100%' : 600
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
              sx: {
                '& input': {
                  py: '10px',
                },
              },
            }}
          />
          {scopeEditable && (
            <TextField
              select size="small" variant="filled" label="Applies to"
              value={channelFilter}
              onChange={(e) => setChannelFilter(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 150, width: isMobile ? '100%' : 'auto' }}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="both">Both</MenuItem>
              <MenuItem value="web_widget">Web widget</MenuItem>
              <MenuItem value="chat">Support chat</MenuItem>
            </TextField>
          )}
        </Box>
        <Box
          display="flex"
          alignItems="center"
          gap={1}
          width={isMobile ? '100%' : 'auto'}
        >
          <OutlinedExportButton
            onClick={handleExport}
            disabled={!rules.length}
            tooltip="Export list as JSON"
            fullWidth={isMobile}
            size="medium"
          />
          <OutlinedExportButton
            onClick={handleImport}
            disabled={!hasAIwritePerm || (editingId != null) || importing}
            tooltip="Import list as JSON"
            label="Import"
            icon={importing ? <CircularProgress size={16} /> : <FileDownloadOutlined />}
            fullWidth={isMobile}
            size="medium"
          />
        </Box>
      </Box>

      {(loading || importing) ? (
        <Box display="flex" alignItems="center" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      ) : isMobile ? (
        // Mobile Card View
        <Box sx={{ maxHeight: 'calc(100vh - 300px)', overflow: 'auto', mb: 2 }}>
          {sortedFiltered.length === 0 && searchQuery.trim() ? (
            <Box display="flex" alignItems="center" justifyContent="center" py={4}>
              <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1rem' }}>
                No rules match your search
              </Typography>
            </Box>
          ) : (
            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable droppableId="rules-table">
                {(provided) => (
                  <Box {...provided.droppableProps} ref={provided.innerRef}>
                    {sortedFiltered.map((rule, index) => {
                      const isEditing = editingId === rule.id;
                      const draft = drafts[rule.id] ?? rule;
                      const rowWrite = canWriteRule(rule);
                      const rowDelete = canDeleteRule(rule);

                      return (
                        <Draggable
                          key={rule.id}
                          draggableId={String(rule.id)}
                          index={index}
                          isDragDisabled={orderBy !== 'priority' || !rowWrite || isEditing}
                        >
                          {(provided, snapshot) => (
                            <Card
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              sx={{
                                mb: 2,
                                opacity: snapshot.isDragging ? 0.8 : 1,
                                backgroundColor: snapshot.isDragging ? 'rgba(0, 0, 0, 0.04)' : 'inherit',
                                ...provided.draggableProps.style,
                              }}
                            >
                              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                                {/* Header with trigger and actions. No select checkbox on
                                    mobile — bulk delete is desktop-only (see the isMobile
                                    guard on "Delete Selected"); the per-row Delete below
                                    covers the mobile case. */}
                                <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1.5, gap: 1 }}>
                                  <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                      {isEditing ? (
                                        <TextFieldSmall
                                          placeholder="@reactivate"
                                          value={draft.intent_label}
                                          onChange={(v) =>
                                            setDrafts((d) => ({ ...d, [rule.id]: { ...(d[rule.id] ?? rule), intent_label: v } }))
                                          }
                                          disabled={!rowWrite}
                                        />
                                      ) : (
                                        <Typography 
                                          variant="h6" 
                                          sx={{ 
                                            fontSize: '1rem', 
                                            fontWeight: 600,
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                            flex: 1,
                                            minWidth: 0,
                                          }}
                                        >
                                          @{(rule.intent_label || '').replace(/^@/, '')}
                                        </Typography>
                                      )}
                                      {orderBy === 'priority' && !isEditing && (
                                        <Box
                                          {...provided.dragHandleProps}
                                          sx={{
                                            cursor: rowWrite ? 'grab' : 'default',
                                            display: 'flex',
                                            alignItems: 'center',
                                            ml: 'auto',
                                            flexShrink: 0,
                                          }}
                                        >
                                          <DragIndicator fontSize="small" sx={{ color: 'text.secondary' }} />
                                        </Box>
                                      )}
                                    </Box>
                                    {isEditing ? (
                                      <TextFieldSmall
                                        placeholder="Short description"
                                        value={draft.intent_description}
                                        onChange={(v) =>
                                          setDrafts((d) => ({ ...d, [rule.id]: { ...(d[rule.id] ?? rule), intent_description: v } }))
                                        }
                                        disabled={!rowWrite}
                                      />
                                    ) : (
                                      <Typography 
                                        variant="body2" 
                                        color="text.secondary" 
                                        sx={{ 
                                          mb: 1,
                                          wordBreak: 'break-word',
                                          overflowWrap: 'break-word',
                                        }}
                                      >
                                        {rule.intent_description || '—'}
                                      </Typography>
                                    )}
                                  </Box>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0, marginTop: '-2.5px' }}>
                                    {isEditing ? (
                                      <>
                                        <IconButton size="small" onClick={cancelEdit} title="Cancel">
                                          <Close fontSize="small" />
                                        </IconButton>
                                        <IconButton size="small" onClick={() => saveRule(rule.id)} title="Save" disabled={!rowWrite}>
                                          <Save fontSize="small" />
                                        </IconButton>
                                      </>
                                    ) : (
                                      <>
                                        <IconButton size="small" onClick={() => startEdit(rule)} title="Edit" disabled={!rowWrite}>
                                          <Edit fontSize="small" />
                                        </IconButton>
                                        <DeleteIconButton onClick={() => handleDeleteRule(rule)} allowed={rowDelete} />
                                      </>
                                    )}
                                  </Box>
                                </Box>

                                {/* Tours */}
                                {showToursCol && (
                                <Box sx={{ mb: 1.5 }}>
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                    Tours:
                                  </Typography>
                                  {isEditing ? (
                                    <>
                                    {showTagPickers && (
                                      <RuleTagPickers
                                        scope={tagScope}
                                        draft={draft}
                                        disabled={!rowWrite}
                                        onChange={(patch) => patchDraft(rule, patch)}
                                      />
                                    )}
                                    <RemoteAutocomplete
                                      disabled={!rowWrite}
                                      multiple
                                      style={{ backgroundColor: 'white', width: '100%' }}
                                      resource={TOURS_RESOURCE}
                                      apiParams={{ fields: 'id,name,is_bespoke,start_date,end_date' }}
                                      value={(draft.tours || []).map(normalizeTour)}
                                      onChange={(e, v) =>
                                        setDrafts((d) => ({ ...d, [rule.id]: { ...(d[rule.id] ?? rule), tours: (v || []).map(normalizeTour) } }))
                                      }
                                      setOptionsCount={() => {}}
                                      setOptions={(opts) =>
                                        setTourOptionsByRule((o) => ({ ...o, [rule.id]: (opts || []).map(normalizeTour) }))
                                      }
                                      filterOptions={(options) => options}
                                      renderTags={(value, getTagProps) =>
                                        value.map((option, index) => (
                                          <Chip
                                            {...getTagProps({ index })}
                                            label={tourToLabel(option)}
                                            size="small"
                                            sx={{
                                              height: 'auto',
                                              '& .MuiChip-label': {
                                                whiteSpace: 'normal',
                                                wordBreak: 'break-word',
                                                display: 'block',
                                                px: 0.5,
                                                py: 0.25,
                                                fontSize: 12,
                                                padding: "3px 8px",
                                              }
                                            }}
                                          />
                                        ))
                                      }
                                      size="small"
                                      label={toursLabel}
                                      variant="outlined"
                                      sx={{
                                        width: '100%',
                                        '& .MuiInputBase-root': { minHeight: 32, py: 0.25, flexWrap: 'wrap' },
                                        '& .MuiFormLabel-root': { fontSize: 12 },
                                      }}
                                      textFieldProps={{
                                        InputProps: { sx: { minHeight: 32, py: 0.25, flexWrap: 'wrap' } },
                                        InputLabelProps: {
                                          sx: {
                                            fontSize: 12,
                                            lineHeight: 1.2,
                                            transform: 'translate(14px, 10px) scale(1)',
                                            '&.MuiInputLabel-shrink': {
                                              transform: 'translate(14px, -6px) scale(0.85)',
                                            },
                                          },
                                        },
                                        FormHelperTextProps: { sx: { fontSize: 11 } },
                                      }}
                                      clearOnBlur={false}
                                      noOptionsText={(tourQueryByRule[rule.id] || '').trim()
                                        ? `No results for "${(tourQueryByRule[rule.id] || '').trim()}"`
                                        : 'Type to search'}
                                      getOptionLabel={(opt) => tourToLabel(opt)}
                                      isOptionEqualToValue={(opt, val) => toursEqual(opt, val)}
                                      onQueryChange={(q) => setTourQueryByRule((prev) => ({ ...prev, [rule.id]: q }))}
                                    />
                                    <ExceptToursPicker
                                      disabled={!rowWrite}
                                      value={draft.excluded_tours || []}
                                      onChange={(v) => patchDraft(rule, { excluded_tours: v })}
                                    />
                                    </>
                                  ) : (
                                    <RuleScopeChips rule={rule} scope={tagScope} />
                                  )}
                                </Box>
                                )}

                                {/* Message */}
                                <Box sx={{ mb: 1.5 }}>
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                    Message:
                                  </Typography>
                                  {isEditing ? (
                                    <TextAreaSmall
                                      placeholder="Reply message to send when intent matches"
                                      value={draft.reply_message}
                                      onChange={(v) =>
                                        setDrafts((d) => ({ ...d, [rule.id]: { ...(d[rule.id] ?? rule), reply_message: v } }))
                                      }
                                      disabled={!rowWrite}
                                    />
                                  ) : (
                                    <Box
                                      sx={{
                                        maxHeight: 120,
                                        overflow: 'auto',
                                        p: 1,
                                        borderRadius: 1,
                                        bgcolor: 'grey.50',
                                        fontSize: '0.875rem',
                                        lineHeight: 1.35,
                                        whiteSpace: 'pre-wrap',
                                        wordBreak: 'break-word',
                                      }}
                                    >
                                      <span
                                        dangerouslySetInnerHTML={{ __html: linkify(escape_html(rule.reply_message || '')) }}
                                      />
                                    </Box>
                                  )}
                                </Box>

                                {/* Enabled */}
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                  <Typography variant="body2">Enabled</Typography>
                                  <Switch
                                    size="small"
                                    checked={!!rule.is_enabled}
                                    onChange={(e) => toggleEnabled(rule, e.target.checked)}
                                    disabled={String(rule.id).startsWith('local-') || !rowWrite}
                                    inputProps={{ 'aria-label': 'toggle enabled' }}
                                  />
                                </Box>

                                {isWidgetTable && (
                                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1 }}>
                                    <Tooltip title={PASS_TO_TEAM_HELP}>
                                      <Typography variant="body2">Pass to team</Typography>
                                    </Tooltip>
                                    {handToTeamSwitch(rule, isEditing, draft, rowWrite)}
                                  </Box>
                                )}

                                {/* Channel scope. Editable only in legacy (unpinned) mode;
                                    pinned pages show a read-only chip so 'both' (shared)
                                    rules are still marked. */}
                                {showScopeCol && (
                                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1 }}>
                                    <Typography variant="body2">Applies to</Typography>
                                    {isEditing && scopeEditable ? (
                                      <TextField
                                        select size="small" variant="standard" sx={{ minWidth: 140 }}
                                        value={draft.source_scope || defaultScope}
                                        onChange={(e) =>
                                          setDrafts((d) => ({ ...d, [rule.id]: { ...(d[rule.id] ?? rule), source_scope: e.target.value } }))
                                        }
                                      >
                                        <MenuItem value="both">Both</MenuItem>
                                        <MenuItem value="web_widget">Web widget</MenuItem>
                                        <MenuItem value="chat">Support chat</MenuItem>
                                      </TextField>
                                    ) : (
                                      <Chip
                                        size="small" variant="outlined"
                                        color={rule.source_scope === 'web_widget' ? 'primary' : 'default'}
                                        label={SCOPE_LABELS[rule.source_scope || 'both']}
                                      />
                                    )}
                                  </Box>
                                )}
                              </CardContent>
                            </Card>
                          )}
                        </Draggable>
                      );
                    })}
                    {provided.placeholder}
                  </Box>
                )}
              </Droppable>
            </DragDropContext>
          )}
        </Box>
      ) : (
        // Desktop Table View
        <TableContainer
          component={Paper}
          ref={tableContainerRef}
          sx={{
            maxHeight: 'calc(100vh - 300px)', // Adjust based on your layout
            overflow: 'auto',
            mb: 2,
            '& td, & th': { py: 0.5, px: 1 },            // tighter vertical + horizontal padding
            '& .MuiTableCell-paddingCheckbox': { px: 0.5 }
          }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ '& .MuiTableCell-head': { py: 1.5 } }}>
                <TableCell padding="checkbox">
                  <input type="checkbox" checked={!!allSelected} onChange={(e) => toggleAll(e.target.checked)} disabled={!hasAIlistPerm}/>
                </TableCell>

                {/* Priority */}
                {/*
                <TableCell sortDirection={orderBy === 'priority' ? order : false} sx={{ width: 120 }}>
                  <TableSortLabel
                    active={orderBy === 'priority'}
                    direction={orderBy === 'priority' ? order : 'asc'}
                    onClick={() => {
                      const isAsc = orderBy === 'priority' && order === 'asc';
                      setOrder(isAsc ? 'desc' : 'asc');
                      setOrderBy('priority');
                    }}
                  >
                    Priority
                  </TableSortLabel>
                </TableCell>
                */}
                {/* Trigger */}
                <TableCell>Trigger</TableCell>

                {/* Description */}
                <TableCell sortDirection={orderBy === 'intent_description' ? order : false}>
                  <TableSortLabel
                    active={orderBy === 'intent_description'}
                    direction={orderBy === 'intent_description' ? order : 'asc'}
                    onClick={() => {
                      const isAsc = orderBy === 'intent_description' && order === 'asc';
                      setOrder(isAsc ? 'desc' : 'asc');
                      setOrderBy('intent_description');
                    }}
                  >
                    Description
                  </TableSortLabel>
                </TableCell>
                {showToursCol && <TableCell>{toursHeader}</TableCell>}
                {showWidgetsCol && <TableCell>Widgets</TableCell>}
                <TableCell>Message</TableCell>
                <TableCell sx={{ width: 110 }}>Enabled</TableCell>
                {isWidgetTable && (
                  <TableCell sx={{ width: 120 }}>
                    <Tooltip title={PASS_TO_TEAM_HELP}>
                      <span>Pass to team</span>
                    </Tooltip>
                  </TableCell>
                )}
                {showScopeCol && <TableCell sx={{ width: 150 }}>Applies to</TableCell>}
                <TableCell align="center">
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>

            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable droppableId="rules-table">
                {(provided) => (
                  <TableBody {...provided.droppableProps} ref={provided.innerRef}>
                      {sortedFiltered.length === 0 && searchQuery.trim() ? (
                       <TableRow height={80}>
                          <TableCell
                            colSpan={6 + (showToursCol ? 1 : 0) + (showWidgetsCol ? 1 : 0) + (isWidgetTable ? 1 : 0)
                              + (showScopeCol ? 1 : 0)}
                            align="center"
                            sx={{ py: 4 }}
                          >
                            <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1rem' }}>
                              No rules match your search
                            </Typography>
                          </TableCell>
                      </TableRow>
                    ) : (
                      sortedFiltered.map((rule, index) => {
                        const isEditing = editingId === rule.id;
                        const draft = drafts[rule.id] ?? rule;
                        const rowWrite = canWriteRule(rule);
                        const rowDelete = canDeleteRule(rule);

                        return (
                          <Draggable
                            key={rule.id}
                            draggableId={String(rule.id)}
                            index={index}
                            isDragDisabled={orderBy !== 'priority' || !rowWrite || isEditing}
                          >
                            {(provided, snapshot) => (
                              <TableRow 
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                hover
                                sx={{
                                  ...provided.draggableProps.style,
                                  opacity: snapshot.isDragging ? 0.8 : 1,
                                  backgroundColor: snapshot.isDragging ? 'rgba(0, 0, 0, 0.04)' : 'inherit',
                                }}
                              >
                                <TableCell padding="checkbox">
                                  <input type="checkbox" checked={!!selection[rule.id]} onChange={() => toggleOne(rule.id)} />
                                </TableCell>

                                {/* Priority value */}
                                {/*
                                <TableCell>
                                  <Typography variant="body2">{rule.priority}</Typography>
                                </TableCell>
                                */}
                                {/* Trigger */}
                                <TableCell sx={{ minWidth: 180 }}>
                                  {isEditing ? (
                                    <TextFieldSmall
                                      placeholder="@reactivate"
                                      value={draft.intent_label}
                                      onChange={(v) =>
                                        setDrafts((d) => ({ ...d, [rule.id]: { ...(d[rule.id] ?? rule), intent_label: v } }))
                                      }
                                      disabled={!rowWrite}
                                    />
                                  ) : (
                                    <Typography variant="body2">@{(rule.intent_label || '').replace(/^@/, '')}</Typography>
                                  )}
                                </TableCell>

                                {/* Description */}
                                <TableCell sx={{ minWidth: 180 }}>
                                  {isEditing ? (
                                    <TextFieldSmall
                                      placeholder="Short description"
                                      value={draft.intent_description}
                                      onChange={(v) =>
                                        setDrafts((d) => ({ ...d, [rule.id]: { ...(d[rule.id] ?? rule), intent_description: v } }))
                                      }
                                      disabled={!rowWrite}
                                    />
                                  ) : (
                                    <Typography variant="body2">{rule.intent_description || '—'}</Typography>
                                  )}
                                </TableCell>

                                {/* Tours */}
                                {showToursCol && (
                                <TableCell sx={{ minWidth: 240 }}>
                                  {isEditing ? (
                                    <>
                                    {showTagPickers && (
                                      <RuleTagPickers
                                        scope={tagScope}
                                        draft={draft}
                                        disabled={!rowWrite}
                                        onChange={(patch) => patchDraft(rule, patch)}
                                      />
                                    )}
                                    <RemoteAutocomplete
                                      disabled={!rowWrite}
                                      multiple
                                      style={{backgroundColor: 'white'}}
                                      resource={TOURS_RESOURCE}
                                      apiParams={{ fields: 'id,name,is_bespoke,start_date,end_date' }}
                                      value={(draft.tours || []).map(normalizeTour)}
                                      onChange={(e, v) =>
                                        setDrafts((d) => ({ ...d, [rule.id]: { ...(d[rule.id] ?? rule), tours: (v || []).map(normalizeTour) } }))
                                      }
                                      setOptionsCount={() => {}}
                                      setOptions={(opts) =>
                                        setTourOptionsByRule((o) => ({ ...o, [rule.id]: (opts || []).map(normalizeTour) }))
                                      }
                                      filterOptions={(options) => options}
                                      renderTags={(value, getTagProps) =>
                                        value.map((option, index) => (
                                          <Chip
                                            {...getTagProps({ index })}
                                            label={tourToLabel(option)}
                                            size="small"
                                            sx={{
                                              height: 'auto',
                                              maxWidth: '180px',
                                              '& .MuiChip-label': {
                                                whiteSpace: 'normal',
                                                wordBreak: 'break-word',
                                                display: 'block',
                                                px: 0.5,
                                                py: 0.25,
                                                fontSize: 12,
                                                padding: "3px 8px",
                                              }
                                            }}
                                          />
                                        ))
                                      }
                                      size="small"
                                      label={toursLabel}
                                      variant="outlined"
                                      sx={{
                                        minWidth: 200,
                                        '& .MuiInputBase-root': { minHeight: 32, py: 0.25, flexWrap: 'wrap' },        // input height
                                        '& .MuiFormLabel-root': { fontSize: 12 },                   // label size
                                      }}
                                      textFieldProps={{
                                        // ensures compact input padding even inside filled/outlined variants
                                        InputProps: { sx: { minHeight: 32, py: 0.25, flexWrap: 'wrap' } },
                                        InputLabelProps: {
                                          sx: {
                                            fontSize: 12,                // ↓ smaller label
                                            lineHeight: 1.2,
                                            // keep good positions for small/outlined/filled
                                            transform: 'translate(14px, 10px) scale(1)',
                                            '&.MuiInputLabel-shrink': {
                                              transform: 'translate(14px, -6px) scale(0.85)',
                                            },
                                          },
                                        },
                                        // (optional) smaller helper text too
                                        FormHelperTextProps: { sx: { fontSize: 11 } },
                                      }}
                                      clearOnBlur={false}
                                      noOptionsText={(tourQueryByRule[rule.id] || '').trim()
                                        ? `No results for "${(tourQueryByRule[rule.id] || '').trim()}"`
                                        : 'Type to search'}
                                      getOptionLabel={(opt) => tourToLabel(opt)}
                                      isOptionEqualToValue={(opt, val) => toursEqual(opt, val)}
                                      onQueryChange={(q) => setTourQueryByRule((prev) => ({ ...prev, [rule.id]: q }))}
                                    />
                                    <ExceptToursPicker
                                      compact
                                      disabled={!rowWrite}
                                      value={draft.excluded_tours || []}
                                      onChange={(v) => patchDraft(rule, { excluded_tours: v })}
                                    />
                                    </>
                                  ) : (
                                    <RuleScopeChips rule={rule} scope={tagScope} />
                                  )}
                                </TableCell>
                                )}

                                {/* Widgets (CP-8335) */}
                                {showWidgetsCol && (
                                <TableCell sx={{ minWidth: 200 }}>
                                  {isEditing ? (
                                    <WidgetsPicker
                                      widgets={widgets}
                                      disabled={!rowWrite}
                                      value={draft.widgets || []}
                                      onChange={(v) =>
                                        setDrafts((d) => ({ ...d, [rule.id]: { ...(d[rule.id] ?? rule), widgets: v } }))
                                      }
                                    />
                                  ) : (
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: .5 }}>
                                      {(rule.widgets || []).map((w) => (
                                        <Chip key={`w${w.id}`} label={widgetLabel(w)} size="small" color="primary" variant="outlined" sx={collectionChipSx} />
                                      ))}
                                      {!rule.widgets?.length &&
                                        <Typography variant="caption" color="text.secondary">All widgets</Typography>}
                                    </Box>
                                  )}
                                </TableCell>
                                )}

                                {/* Message */}
                                <TableCell sx={{ minWidth: 360, maxWidth: 560 }}>
                                  {isEditing ? (
                                    <TextAreaSmall
                                      placeholder="Reply message to send when intent matches"
                                      value={draft.reply_message}
                                      onChange={(v) =>
                                        setDrafts((d) => ({ ...d, [rule.id]: { ...(d[rule.id] ?? rule), reply_message: v } }))
                                      }
                                      disabled={!rowWrite}
                                    />
                                  ) : (
                                    <Box
                                      sx={{
                                        maxHeight: 160,            // tweak: how tall before scrolling
                                        overflow: 'auto',
                                        p: 0.5,
                                        borderRadius: 1,
                                        fontSize: '0.875rem',
                                        lineHeight: 1.35,
                                        whiteSpace: 'pre-wrap',    // PRESERVES NEWLINES
                                        wordBreak: 'break-word',   // wraps long words/URLs
                                      }}
                                    >
                                      <span
                                        // only enable if your HTML is sanitized
                                        dangerouslySetInnerHTML={{ __html: linkify(escape_html(rule.reply_message || '')) }}
                                      />
                                    </Box>
                                  )}
                                </TableCell>

                                {/* Enabled */}
                                <TableCell>
                                  <Switch
                                    size="small"
                                    checked={!!rule.is_enabled}
                                    onChange={(e) => toggleEnabled(rule, e.target.checked)}
                                    disabled={String(rule.id).startsWith('local-') || !rowWrite}
                                    sx={{ my: -0.25 }} // keep row compact
                                    inputProps={{ 'aria-label': 'toggle enabled' }}
                                  />
                                </TableCell>

                                {/* Pass to team (web chat only) */}
                                {isWidgetTable && (
                                  <TableCell>{handToTeamSwitch(rule, isEditing, draft, rowWrite, { my: -0.25 })}</TableCell>
                                )}

                                {/* Channel scope. Editable only in legacy (unpinned) mode;
                                    pinned pages show a read-only chip. */}
                                {showScopeCol && (
                                  <TableCell>
                                    {isEditing && scopeEditable ? (
                                      <TextField
                                        select size="small" variant="standard" sx={{ minWidth: 120 }}
                                        value={draft.source_scope || defaultScope}
                                        onChange={(e) =>
                                          setDrafts((d) => ({ ...d, [rule.id]: { ...(d[rule.id] ?? rule), source_scope: e.target.value } }))
                                        }
                                      >
                                        <MenuItem value="both">Both</MenuItem>
                                        <MenuItem value="web_widget">Web widget</MenuItem>
                                        <MenuItem value="chat">Support chat</MenuItem>
                                      </TextField>
                                    ) : (
                                      <Chip
                                        size="small"
                                        variant="outlined"
                                        color={rule.source_scope === 'web_widget' ? 'primary' : 'default'}
                                        label={SCOPE_LABELS[rule.source_scope || 'both']}
                                      />
                                    )}
                                  </TableCell>
                                )}

                                {/* Action */}
                                <TableCell align="left" sx={{ whiteSpace: 'nowrap' }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    {/* Drag handle - only show when sorted by priority */}
                                    {orderBy === 'priority' && !isEditing && (
                                      <div 
                                        {...provided.dragHandleProps} 
                                        style={{ 
                                          cursor: rowWrite ? 'grab' : 'default',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          padding: '4px'
                                        }}
                                        title={rowWrite ? 'Drag to reorder' : ''}
                                      >
                                        <DragIndicator 
                                          fontSize="small" 
                                          sx={{ 
                                            color: rowWrite ? 'text.secondary' : 'action.disabled',
                                            '&:hover': { 
                                              color: rowWrite ? 'text.primary' : 'action.disabled',
                                              transform: rowWrite ? 'scale(1.2)' : 'none',
                                              transition: 'all 0.2s ease'
                                            },
                                            transition: 'all 0.2s ease'
                                          }}
                                        />
                                      </div>
                                    )}
                                    {isEditing ? (
                                      <>
                                        <IconButton size="small" onClick={cancelEdit} title="Cancel">
                                          <Close fontSize="small" />
                                        </IconButton>
                                        <IconButton size="small" onClick={() => saveRule(rule.id)} title="Save" disabled={!rowWrite}>
                                          <Save fontSize="small" />
                                        </IconButton>
                                      </>
                                    ) : (
                                      <>
                                        <IconButton size="small" onClick={() => startEdit(rule)} title="Edit" disabled={!rowWrite}>
                                          <Edit fontSize="small" />
                                        </IconButton>
                                        <DeleteIconButton onClick={() => handleDeleteRule(rule)} allowed={rowDelete} />
                                      </>
                                    )}
                                  </Box>
                                </TableCell>
                              </TableRow>
                            )}
                          </Draggable>
                        );
                      })
                    )}
                    {provided.placeholder}
                  </TableBody>
                )}
              </Droppable>
            </DragDropContext>
          </Table>
        </TableContainer>
      )}

      {ruleToDelete && (
        <ConfirmationDialog
          open={!!ruleToDelete}
          onCancel={cancelDeleteRule}
          onConfirm={confirmDeleteRule}
          title="Please confirm"
          okButtonText="Delete"
          cancelButtonText="Cancel"
        >
          Are you sure you want to delete this trigger-reply rule?
        </ConfirmationDialog>
      )}

      {rulesToDelete && rulesToDelete.length > 0 && (
        <ConfirmationDialog
          open={!!rulesToDelete}
          onCancel={cancelBulkDelete}
          onConfirm={confirmBulkDelete}
          title="Please confirm"
          okButtonText="Delete"
          cancelButtonText="Cancel"
        >
          Are you sure you want to delete {rulesToDelete.length} selected trigger-reply rule{rulesToDelete.length > 1 ? 's' : ''}?
        </ConfirmationDialog>
      )}
    </Paper>
  );
}

/* tiny controlled inputs to keep code tidy */
function TextFieldSmall({ value, onChange, placeholder, disabled }) {
  return (
    <input
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      disabled={disabled}
      style={{ width: '96%', paddingTop: 8, paddingBottom: 8, paddingLeft: 8, borderRadius: 4, border: '1px solid rgba(0,0,0,0.23)' }}
    />
  );
}

function TextAreaSmall({ value, onChange, placeholder, disabled }) {
  return (
    <textarea
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      rows={6}
      disabled={disabled}
      style={{ width: '100%', paddingTop: 8, paddingBottom: 8, paddingLeft: 8, borderRadius: 4, border: '1px solid rgba(0,0,0,0.23)', resize: 'vertical' }}
    />
  );
}

export default React.memo(AutoReplyRulesTable);
